import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, InputGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaSearch, FaCalendarAlt, FaUser, FaTags } from 'react-icons/fa';
import axios from 'axios';
import './Blog.css';

const Blog = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Categories for filtering
  const categories = [
    'All',
    'Web Development',
    'Machine Learning',
    'Data Science',
    'DevOps',
    'Backend Development'
  ];

  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        const response = await axios.get('/api/blog');
        setBlogPosts(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching blog posts:', err);
        setError('Failed to load blog posts. Please try again later.');
        setLoading(false);
      }
    };

    fetchBlogPosts();
  }, []);

  // Filter blog posts based on search term and category
  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  // Featured posts
  const featuredPosts = blogPosts.filter(post => post.featured);

  if (loading) {
    return (
      <Container className="py-5 mt-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading blog posts...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5 mt-5">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </Container>
    );
  }

  return (
    <div className="blog-page">
      <section className="blog-hero">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8} className="text-center">
              <h1 className="blog-title">My Blog</h1>
              <p className="blog-subtitle">
                Thoughts, tutorials, and insights on web development, data science, and machine learning.
              </p>
              <InputGroup className="search-bar">
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
          </Row>
        </Container>
      </section>

      {searchTerm === '' && selectedCategory === 'All' && (
        <section className="featured-posts">
          <Container>
            <h2 className="section-title text-center">Featured Posts</h2>
            <div className="section-intro text-center mb-5">
              <p className="lead">
                Highlighted articles on trending topics and technologies
              </p>
            </div>
            <Row>
              {featuredPosts.map(post => (
                <Col lg={6} key={post.id} className="mb-4">
                  <div className="featured-post-card">
                    <div className="featured-post-image" style={{ backgroundImage: `url(${process.env.PUBLIC_URL + post.image})` }}>
                      <div className="featured-post-category">{post.category}</div>
                    </div>
                    <div className="featured-post-content text-left">
                      <h3>{post.title}</h3>
                      <p>{post.excerpt}</p>
                      <div className="post-meta">
                        <span><FaCalendarAlt /> {post.date}</span>
                        <span><FaUser /> {post.author}</span>
                      </div>
                      <Link to={`/blog/${post.id}`} className="read-more-btn">Read More</Link>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>
      )}

      <section className="all-posts">
        <Container>
          <Row>
            <Col lg={9}>
              <div className="posts-header text-left">
                <h2 className="section-title">
                  {searchTerm !== '' ? 'Search Results' :
                   selectedCategory !== 'All' ? `${selectedCategory} Articles` : 'All Articles'}
                </h2>
                <div className="posts-count">{filteredPosts.length} articles</div>
              </div>

              <Row>
                {filteredPosts.map(post => (
                  <Col lg={6} md={6} key={post.id} className="mb-4">
                    <div className="blog-card">
                      <div className="blog-card-image" style={{ backgroundImage: `url(${process.env.PUBLIC_URL + post.image})` }}>
                        <div className="blog-card-category">{post.category}</div>
                      </div>
                      <div className="blog-card-content text-left">
                        <h3>{post.title}</h3>
                        <p>{post.excerpt}</p>
                        <div className="post-meta">
                          <span><FaCalendarAlt /> {post.date}</span>
                          <span><FaUser /> {post.author}</span>
                        </div>
                        <div className="post-tags">
                          <FaTags />
                          {post.tags.map((tag, index) => (
                            <span key={index} className="post-tag">{tag}</span>
                          ))}
                        </div>
                        <Link to={`/blog/${post.id}`} className="read-more-btn">Read More</Link>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>

              {filteredPosts.length === 0 && (
                <div className="no-results">
                  <h3>No articles found</h3>
                  <p>Try adjusting your search or filter criteria.</p>
                </div>
              )}
            </Col>

            <Col lg={3}>
              <div className="blog-sidebar text-left">
                <div className="sidebar-widget">
                  <h4 className="widget-title">Categories</h4>
                  <ul className="category-list">
                    {categories.map((category, index) => (
                      <li
                        key={index}
                        className={selectedCategory === category ? 'active' : ''}
                        onClick={() => setSelectedCategory(category)}
                      >
                        {category}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="sidebar-widget">
                  <h4 className="widget-title">Recent Posts</h4>
                  <ul className="recent-posts">
                    {blogPosts.slice(0, 3).map(post => (
                      <li key={post.id}>
                        <a href="#!">
                          <div className="recent-post-image" style={{ backgroundImage: `url(${process.env.PUBLIC_URL + post.image})` }}></div>
                          <div className="recent-post-info">
                            <h5>{post.title}</h5>
                            <span>{post.date}</span>
                          </div>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default Blog;
